import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { XIcon } from "lucide-react";
import {
  getAuctionEventById,
  getLatestBid,
  getAllBidsForBanker,
  getAllWrongBidsForBanker,
  amountInWords,
  convertAmmountToWords,
  amountFormatter,
  updateAuctionEventStatus,
} from "services";

import { Button } from "../../../shadcn/components/ui/button";
import CheckIcon from "../../../icons/CheckIcon";
import ViewEventCardWrapper from "./ViewEventCardWrapper";
import CalendarSingleDotIcon from "../../../icons/CalendarSingleDotIcon";
import BidHistoryModel from "../../bidder/Tabs/auctionFloor/components/BidHistoryModel";

export default function ViewEvent2() {
  const router = useRouter();
  const { id } = router.query;
  const [event, setEvent] = useState<any>([]);
  const [lastBid, setLastBid] = useState<any>(null);
  const [correctBids, setCorrectBids] = useState<any[]>([]);
  const [showBidHistoryModel, setShowBidHistoryModel] = useState(false);
  const [wrongBids, setWrongBids] = useState<any[]>([]);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const userProfile = useSelector((state: any) => {
    return state.bankerAuth.user;
  });

  useEffect(() => {
    if (id) {
      const fetchEventData = async () => {
        try {
          const data: any = await getAuctionEventById(id);
          setEvent(data);
        } catch (err) {
          console.log("error", err);
        }
      };

      fetchEventData();
    }
  }, [id]);

  useEffect(() => {
    if (!event || event?.status !== "end") return;
    if (event) {
      const fetchBidsData = async () => {
        try {
          const lastBids: any = await getLatestBid(event.uuid);
          if (lastBids) {
            setLastBid(lastBids);
          }
          const allBids: any = await getAllBidsForBanker(event.uuid);
          if (allBids) {
            setCorrectBids(allBids);
          }
          const allWrongBids: any = await getAllWrongBidsForBanker(event.uuid);
          if (allWrongBids) {
            setWrongBids(allWrongBids);
          }
        } catch (err) {
          console.log("error", err);
        }
      };

      fetchBidsData();
    }
  }, [event]);

  const biddingData = [
    {
      title: "Highest Bid",
      value: lastBid && lastBid?.final_amount,
    },
    { title: "Highest Bid By", value: lastBid && lastBid?.meta?.email },
    {
      title: "Highest Bid At",
      value:
        lastBid &&
        lastBid?.frontend_timestamp &&
        `${DateTime.fromMillis(
          typeof lastBid?.frontend_timestamp === "string"
            ? parseInt(lastBid?.frontend_timestamp)
            : lastBid?.frontend_timestamp,
        )
          .setZone("Asia/Kolkata")
          .toFormat("DD hh:mm:ss a")} (IST)`,
    },
    {
      title: "Auction Ended At",
      value:
        lastBid &&
        lastBid?.final_end_time &&
        `${DateTime.fromMillis(
          typeof lastBid?.final_end_time === "string"
            ? parseInt(lastBid?.final_end_time)
            : lastBid?.final_end_time,
        )
          .setZone("Asia/Kolkata")
          .toFormat("DD hh:mm:ss a")} (IST)`,
    },
    { title: "Currency", value: "INR" },
    {
      title: "Number of Extension",
      value:
        lastBid && lastBid.number_of_extension
          ? lastBid.number_of_extension
          : "-",
    },
    {
      title: "Number of Successful Bids",
      value: correctBids && correctBids.length,
    },
  ];

  const eventCompleteData = [
    {
      heading: "Event Details",
      data: [
        { title: "Event Name", value: event?.event_name },

        {
          title: "Approver",
          value: event?.meta?.approver?.[0]?.name
            ? event?.meta?.approver?.[0]?.name
            : event?.meta?.approver?.[0]?.email
            ? event?.meta?.approver?.[0]?.email
            : "",
        },
        { title: "Event Type", value: event?.event_type },
        { title: "Signature Type", value: event?.signature_type },
        { title: "Auction Currency", value: event?.auction_currency },
        { title: "Possession Type", value: event?.possession_type },
        { title: "KYC Type", value: event?.kyc_type },
      ],
    },
    {
      heading: "Property Details",
      data: [
        { title: "Property ID", value: event?.property_dump?.property_id },
        { title: "Property State", value: event?.property_dump?.state.name },
        { title: "Reserve Price", value: event?.property_dump?.reserve_price },
        {
          title: "Reserve Price in Words",
          value: convertAmmountToWords(event?.property_dump?.reserve_price),
        },
        { title: "EMD Price", value: event?.property_dump?.emd_amount },
        {
          title: "EMD Price in Words",
          value: convertAmmountToWords(event?.property_dump?.emd_amount),
        },
        {
          title: "Property Address",
          value: event?.property_dump?.address,
        },
        {
          title: "EMD Last Date and Time(IST)",
          value: DateTime.fromISO(event?.property_dump?.emd_last_date)
            .setZone("Asia/Kolkata")
            .toFormat("MMM dd, yyyy, hh:mm a"),
        },
        {
          title: "Auction Start Date and Time(IST)",
          value: DateTime.fromISO(event?.property_dump?.auction_start_date_time)
            .setZone("Asia/Kolkata")
            .toFormat("MMM dd, yyyy, hh:mm a"),
        },
        {
          title: "Auction End Date and Time(IST)",
          value: DateTime.fromISO(event?.property_dump?.auction_end_date_time)
            .setZone("Asia/Kolkata")
            .toFormat("MMM dd, yyyy, hh:mm a"),
        },
        { title: "Auto Extension", value: event?.auto_extension },
        { title: "Auto Extension Type", value: event?.auto_extension_type },
        { title: "Auto Extension Limit", value: event?.auto_extension_limit },
        { title: "Extension Method", value: event?.extension_method },
        { title: "Extend Time At", value: event?.extend_time_at },
        { title: "Extend Time By", value: event?.extend_time_by },
        {
          title: "Bidding Increment Amount",
          value: event?.bidding_increment_amount,
        },
        {
          title: "Bidding Increment Amount in Words",
          value: amountInWords(event?.bidding_increment_amount),
        },
      ],
    },
    {
      heading: "Other Details",
      data: [
        { title: "Borrower Name", value: event?.borrower_name },
        {
          title: "Inspection of asset Date and Time",
          value: event?.property_dump?.physical_inspection_timeline,
        },
        {
          title: "Inspection Officer Contact Details",
          value: event?.property_dump?.contact,
        },
        { title: "Remark", value: event?.comment },
      ],
    },
    {
      heading: "EMD Payment Account Details",
      data: [
        {
          title: "Account Name",
          value: event?.emd_payment_account_name,
        },
        {
          title: "Account Number",
          value: event?.emd_payment_account_number,
        },
        {
          title: "Account IFSC",
          value: event?.emd_payment_account_ifsc,
        },
        {
          title: "Account Bank",
          value: event?.emd_payment_account_bank,
        },
        {
          title: "Account Branch",
          value: event?.emd_payment_account_branch,
        },
        { title: "Method", value: event?.emd_payment_method },
        { title: "Bidding Type", value: event?.bidding_type },
      ],
    },
  ];

  const RejectionModal = () => {
    const [cancellationReason, setCancellationReason] = useState("");
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
        <div className="bg-white shadow-2xl  w-full max-w-lg p-6">
          <div className="w-full flex justify-between items-center  pb-3">
            <p className="font-openSans font-semibold text-web-body-lg leading-5 text-brand-bgBlack">
              Reject Event
            </p>
            <button
              onClick={() => {
                setShowRejectionModal(false);
              }}
              className="text-gray-500 bg-brand-bgLightGrey rounded-full p-1.5 hover:text-gray-700"
            >
              <XIcon className="h-6 w-6" />
            </button>
          </div>

          <div className={` relative w-full `}>
            <textarea
              id="name"
              value={cancellationReason}
              onChange={(e: any) => {
                setCancellationReason(e.target.value);
              }}
              className={`bg-white   py-4 peer pl-4 autofill:bg-white focus:border-brand-orange focus:outline-none focus:ring-0 pb-[6px] min-h-[120px]  outline-none  border border-[#EEEEEE]  w-full   font-openSans font-normal  leading-4 text-brand-textLightGrey `}
            />
            <label
              htmlFor="name"
              className={`absolute font-openSans font-normal text-web-body-sm leading-3 mid:text-web-body-lg mid:leading-5 ${
                !cancellationReason
                  ? "text-brand-borderGrey"
                  : "text-transparent hidden"
              } left-4 top-4 pointer-events-none transition-all duration-300 peer-placeholder-shown:top-1.5 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-[10px] peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:leading-[13px] peer-focus:text-brand-orange peer-valid:top-1.5 peer-valid:text-[10px] peer-valid:leading-[13px] peer-valid:text-brand-borderGrey`}
            >
              Reason For Rejecting the Event
            </label>
          </div>

          <div className="w-full flex justify-center mt-6">
            <button
              disabled={isLoading || cancellationReason === ""}
              className={`bg-brand-borderGrey text-white px-6 py-2 lg:w-[225px] ${
                cancellationReason === "" ? "cursor-not-allowed" : ""
              } rounded-lg hover:bg-brand-textGrey `}
              onClick={() => {
                handleRejectEvent(cancellationReason);
              }}
            >
              Reject
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleAcceptEvent = async () => {
    try {
      setIsLoading(true);
      const data: any = await updateAuctionEventStatus(
        event.uuid,
        "accepted",
        "",
      );
      if (data) {
        router.push("/dashboard/events");
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);

      console.log("check here error", error);
    }
  };

  const handleRejectEvent = async (reason: string) => {
    try {
      setIsLoading(true);

      const data: any = await updateAuctionEventStatus(
        event.uuid,
        "rejected",
        reason,
      );
      if (data) {
        router.push("/dashboard/events");
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);

      console.log("check here error", error);
    }
  };

  const handleBidderClick = () => {
    router.push(`/dashboard/bidders?for_event=${event.listing_id}`);
  };
  return (
    <>
      <div className="sm:flex justify-between items-center">
        <p className="font-openSans font-normal text-web-subheading leading-6 text-brand-bgBlack mb-4 sm:mb-0">
          Auction Event
          {id && (
            <span>
              {" ID - "}
              <span className="text-black font-semibold">{id}</span>
            </span>
          )}
        </p>
        <div className="flex space-x-2 md:space-x-3">
          <Button
            onClick={() => {
              router.push(event.pdfs[0]?.download_url);
            }}
            className={`font-openSans ${
              event?.status === "end" && event.pdfs[0]?.download_url
                ? ""
                : "hidden"
            }     font-semibold h-8 md:h-10 md:px-3 px-2 py-0.5 text-[10px] md:text-web-body-sm text-white`}
          >
            Download Report
          </Button>
          <Button
            disabled={
              event?.status !== "end" ||
              (correctBids?.length === 0 && wrongBids?.length == 0)
            }
            onClick={() => {
              setShowBidHistoryModel(true);
            }}
            className="font-openSans font-semibold h-8 md:h-10 md:px-3 px-2 py-0.5 text-[10px] md:text-web-body-sm text-white"
          >
            Bids History
          </Button>
        </div>
      </div>
      <div className="lg:pr-14">
        <ViewEventCardWrapper
          subpartClass="mt-4 lg:mt-[18px]"
          cardDataClass="mt-4 lg:mt-10"
          cardData={biddingData}
        >
          {event?.status === "end" ? (
            correctBids.length === 0 ? (
              <div className="flex flex-col-reverse lg:flex-row lg:items-center lg:justify-between">
                <p className="font-openSans font-normal text-web-body-md lg:text-web-body-lg leading-5 lg:leading-[22px] max-w-[528px]  text-brand-bgBlack mt-4 lg:mt-0">
                  The auction event has been ended successfuly but unfortunately
                  no one participated in the auction.
                </p>
                <button
                  onClick={handleBidderClick}
                  className="font-openSans font-bold text-web-body-sm leading-4 text-brand-textGrey underline ml-auto lg:mr-10"
                >
                  Bidders
                </button>
              </div>
            ) : (
              <div className="flex flex-col-reverse lg:flex-row lg:items-center lg:justify-between">
                <div className="mt-4 lg:mt-0 flex">
                  <CheckIcon />
                  <p className="font-openSans font-normal text-web-body-md lg:text-web-body-lg leading-5 lg:leading-[22px] max-w-[528px] ml-2 lg:ml-[13px] text-brand-bgBlack ">
                    The event has ended successfully and
                    <span className="font-semibold">{` ${
                      lastBid && lastBid?.meta?.email
                    } `}</span>
                    has won the auction with the highest bid of ₹
                    {lastBid && amountFormatter(lastBid.final_amount)}
                  </p>
                </div>
                <button
                  onClick={handleBidderClick}
                  className="font-openSans font-bold text-web-body-sm leading-4 text-brand-textGrey underline ml-auto lg:mr-10"
                >
                  Bidders
                </button>
              </div>
            )
          ) : event?.status === "cancelled" || event?.status === "rejected" ? (
            <div className="flex flex-col-reverse lg:flex-row lg:items-center lg:justify-between">
              <p className="font-openSans font-normal text-web-body-md lg:text-web-body-lg leading-5 lg:leading-[22px] max-w-[528px]  text-brand-bgBlack mt-4 lg:mt-0">
                The auction event has been {event?.status}.
              </p>
              <button
                onClick={handleBidderClick}
                className="font-openSans font-bold text-web-body-sm leading-4 text-brand-textGrey underline ml-auto lg:mr-10"
              >
                Bidders
              </button>
            </div>
          ) : event?.status === "accepted" ? (
            <div className="flex flex-col-reverse lg:flex-row lg:items-center lg:justify-between">
              <div className="mt-4 lg:mt-0 flex">
                <CalendarSingleDotIcon />
                <p className="font-openSans font-normal text-web-body-md lg:text-web-body-lg leading-5 lg:leading-[22px] max-w-[528px] ml-2 lg:ml-[13px] text-brand-bgBlack ">
                  The event is scheduled on{" "}
                  {DateTime.fromISO(
                    event?.property_dump?.auction_start_date_time,
                  )
                    .setZone("Asia/Kolkata")
                    .toFormat("MMM dd, yyyy, hh:mm a")}
                </p>
              </div>
              <button
                onClick={handleBidderClick}
                className="font-openSans font-bold text-web-body-sm leading-4 text-brand-textGrey underline ml-auto lg:mr-10"
              >
                Bidders
              </button>
            </div>
          ) : (
            <div className="flex flex-col-reverse lg:flex-row lg:items-center lg:justify-between">
              <p className="font-openSans font-normal text-web-body-md lg:text-web-body-lg leading-5 lg:leading-[22px] max-w-[528px]  text-brand-bgBlack mt-4 lg:mt-0">
                Event is in pending state and needs approval
              </p>
              <button
                onClick={handleBidderClick}
                className="font-openSans font-bold text-web-body-sm leading-4 text-brand-textGrey underline ml-auto lg:mr-10"
              >
                Bidders
              </button>
            </div>
          )}
        </ViewEventCardWrapper>
        {eventCompleteData.map((subpart: any, index) => (
          <ViewEventCardWrapper
            key={index}
            cardDataClass="mt-6 lg:mt-[14px]"
            subpartClass="mt-6"
            cardData={subpart?.data}
          >
            <p>{subpart?.heading}</p>
          </ViewEventCardWrapper>
        ))}
        {event?.approver === userProfile.uuid &&
          event?.status === "pending" && (
            <div className="w-full flex justify-center md:justify-end md:pr-5 mt-10">
              <div className="space-x-8 md:space-x-12">
                <Button
                  disabled={isLoading}
                  onClick={() => {
                    setShowRejectionModal(true);
                  }}
                  className="bg-brand-bgLightGrey text-[#B60100] px-5 py-3 w-[140px] md:w-[225px] hover:bg-brand-borderLightGray"
                >
                  Reject
                </Button>
                <Button
                  disabled={isLoading}
                  onClick={handleAcceptEvent}
                  className="px-5 py-3 w-[140px] md:w-[225px]"
                >
                  Accept
                </Button>
              </div>
            </div>
          )}
      </div>
      {showRejectionModal && <RejectionModal />}
      <BidHistoryModel
        app="banker"
        open={showBidHistoryModel}
        setOpen={setShowBidHistoryModel}
        bidsEvent={correctBids}
        wrongBidsEvent={wrongBids}
        isSwissAuction={false}
      />
    </>
  );
}
